import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import { ButtonColor } from '@delmonicos/shared-design-system-react/src/components/button/button';
import { Dialog } from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import { Stack } from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import * as cssTable from '@delmonicos/shared-design-system-react/src/components/table/table.module.scss';
import { hasAccessTo } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { TariffModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/outputs/tariff/tariff.model';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import { CellContext } from '@tanstack/react-table';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { APP_PATH } from '@app/config/app-paths';
import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';

import { TariffDeleteController } from '../tariff-delete/tariff-delete.controller';
import { TariffContextProvider } from '../tariff.context';

export interface TariffListActionsCellProps
  extends CellContext<TariffModel, unknown> {}

export const TariffListActionsCell = (
  props: TariffListActionsCellProps,
): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { row } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  const canDelete =
    userPermissions &&
    hasAccessTo(userPermissions, {
      resourceName: Resource.TARIFFS,
      action: ActionsByResource.TARIFFS.DELETE,
    });

  const canRead =
    userPermissions &&
    hasAccessTo(userPermissions, {
      resourceName: Resource.TARIFFS,
      action: ActionsByResource.TARIFFS.READ,
    });

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [actionDeleteIsDialogOpen, setActionDeleteIsDialogOpen] =
    useState(false);

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <TariffContextProvider data={row.original}>
      <Stack justifyContent="flex-end">
        {canRead && (
          <ButtonIcon
            icon="eye"
            color={ButtonColor.Ghost}
            onClick={() =>
              navigate(
                generatePath(APP_PATH.TARIFF.DETAILS, {
                  id: row.original.id,
                }),
              )
            }
            className={cssTable.displayOnHover}
          />
        )}
        {canDelete && (
          <>
            <ButtonIcon
              icon="trash-01"
              color={ButtonColor.Ghost}
              onClick={() => setActionDeleteIsDialogOpen(true)}
              className={cssTable.displayOnHover}
            />
            <Dialog
              onClose={() => setActionDeleteIsDialogOpen(true)}
              open={actionDeleteIsDialogOpen}>
              <Dialog.Header title={t('$.tariff-delete.title')} icon="map-01" />
              <Dialog.Content>
                <TariffDeleteController
                  onCancel={() => setActionDeleteIsDialogOpen(false)}
                  onSuccess={() => setActionDeleteIsDialogOpen(false)}
                />
              </Dialog.Content>
            </Dialog>
          </>
        )}
      </Stack>
    </TariffContextProvider>
  );
};
